import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Stack,
  Divider,
  Chip,
  IconButton,
  Paper,
  CircularProgress,
  Tooltip,
  CardContent,
  useMediaQuery,
  Tabs,
  Tab,
} from "@mui/material";
import {
  PrintOutlined as PrintIcon,
  ArrowBackOutlined as BackIcon,
  RefreshOutlined as RefreshIcon,
  ChatOutlined as ChatIcon,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getApiRoute } from "../services/Config.js";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import MaestroNavbar from "./blocks/MaestroNavbar.js";
import LessonChat from "./LessonChat.js";
import { useLessonStorage } from "../services/LessonStorageService.js";
import DraggableLessonContent from "./DraggableLessonContent.js";
import LessonResources from "./LessonResources.js";

const LessonPlan = () => {
  const { unitDay: unitDayParam } = useParams();
  const unitDay = useMemo(() => parseInt(unitDayParam, 10), [unitDayParam]);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const printRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  const [lessonContent, setLessonContent] = useState("");
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [highlightedChanges, setHighlightedChanges] = useState([]);
  const [saveStatus, setSaveStatus] = useState("");
  const [loadedFromSaved, setLoadedFromSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalContent, setOriginalContent] = useState("");
  const [savedLessonTimestamp, setSavedLessonTimestamp] = useState(null);
  const [chatOpen, setChatOpen] = useState(isLargeScreen);
  const [activeTab, setActiveTab] = useState("lesson-plan");
  const [resources, setResources] = useState(null);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState("");

  // Block addition functionality
  const [addableBlocks, setAddableBlocks] = useState([]);
  const draggableContentRef = useRef(null);

  // Lesson storage hook
  const {
    savedLessons,
    getLatestLessonForDay,
    saveLesson,
    loading: storageLoading,
  } = useLessonStorage();

  useEffect(() => {
    if (storageLoading) {
      console.log("⏳ Waiting for IndexedDB to load...");
      return; // Wait until storage is loaded
    }

    console.log("✅ IndexedDB loaded.");
    const savedLesson = getLatestLessonForDay(unitDay);

    if (savedLesson) {
      console.log("✅ Using saved lesson for Unit Day", unitDay);
      setLessonContent(savedLesson.content);
      setOriginalContent(savedLesson.content);
      setFollowUps([]);
      setLoadedFromSaved(true);
      setSavedLessonTimestamp(savedLesson.dateModified);
      setLoading(false);
    } else {
      console.log("🔁 No saved lesson, fetching from API...");
      fetchLessonPlan();
    }
  }, [storageLoading, unitDay]);

  // Auto-save functionality with debounce
  useEffect(() => {
    if (
      !lessonContent ||
      !originalContent ||
      lessonContent === originalContent
    ) {
      setHasUnsavedChanges(false);
      return;
    }

    setHasUnsavedChanges(true);

    // Debounce auto-save by 2 seconds
    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [lessonContent, originalContent]);

  // Update relative time display every minute
  useEffect(() => {
    if (!savedLessonTimestamp) return;

    const interval = setInterval(() => {
      // Force re-render to update relative time display
      // The getRelativeTime function will recalculate based on current time
      setSavedLessonTimestamp((savedLessonTimestamp) => savedLessonTimestamp);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [savedLessonTimestamp]);

  const handleAutoSave = async () => {
    if (
      !lessonContent ||
      !lessonContent.trim() ||
      lessonContent === originalContent
    ) {
      return;
    }

    try {
      const lessonData = {
        unitDay: unitDay,
        content: lessonContent,
      };

      saveLesson(lessonData);
      setOriginalContent(lessonContent);
      setHasUnsavedChanges(false);
      setLoadedFromSaved(true);
      setSavedLessonTimestamp(new Date().toISOString());

      // Show brief auto-save indicator
      setSaveStatus("Auto-saved");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (error) {
      console.error("Error auto-saving lesson:", error);
    }
  };

  const fetchLessonPlan = async () => {
    try {
      setLoading(true);
      setError("");
      setLoadedFromSaved(false);

      console.log("🌐 Fetching fresh lesson plan for Unit Day", unitDay);

      const response = await axios.get(
        `${getApiRoute("maestro", "LESSON_PLAN", { unitDay })}`
      );

      if (response.data && response.data.response) {
        setLessonContent(response.data.response);
        setOriginalContent(response.data.response);
        setFollowUps(response.data.followUps || []);
        setLoadedFromSaved(false);
        setSavedLessonTimestamp(null);

        // Save the fresh lesson plan to storage
        const lessonData = {
          unitDay: unitDay,
          content: response.data.response,
        };
        saveLesson(lessonData);
        console.log("💾 Saved fresh lesson plan to storage");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("❌ Error fetching lesson plan:", error);
      setError("Unable to load lesson plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSaveLesson = async () => {
    if (!lessonContent || !lessonContent.trim()) {
      setSaveStatus("No lesson content to save");
      setTimeout(() => setSaveStatus(""), 3000);
      return;
    }

    try {
      setSaveStatus("Saving...");

      const lessonData = {
        unitDay: unitDay,
        content: lessonContent,
      };

      const savedId = saveLesson(lessonData);
      setSaveStatus("Lesson saved successfully!");
      setLoadedFromSaved(true); // Mark as saved version
      setSavedLessonTimestamp(new Date().toISOString());

      console.log(`Lesson saved with ID: ${savedId}`);

      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(""), 3000);
    } catch (error) {
      console.error("Error saving lesson:", error);
      setSaveStatus("Error saving lesson");
      setTimeout(() => setSaveStatus(""), 3000);
    }
  };

  const handleLoadFresh = async () => {
    try {
      setLoading(true);
      setError("");
      setLoadedFromSaved(false);

      console.log("Loading fresh lesson plan for Unit Day", unitDay);
      const response = await axios.get(
        `${getApiRoute("maestro", "LESSON_PLAN")}/${unitDay}`
      );

      if (response.data && response.data.response) {
        setLessonContent(response.data.response);
        setOriginalContent(response.data.response); // Set original content for auto-save comparison
        setFollowUps(response.data.followUps || []);
        setLoadedFromSaved(false);
        setSavedLessonTimestamp(null);

        // Save the fresh lesson plan to storage
        const lessonData = {
          unitDay: unitDay,
          content: response.data.response,
        };
        saveLesson(lessonData);
        console.log("💾 Saved fresh lesson plan to storage");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching fresh lesson plan:", error);
      setError("Unable to load fresh lesson plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  // Block addition functionality
  const addBlockToLessonPlan = (block, position = "end") => {
    // Use the draggable content component's addBlock method if available
    if (draggableContentRef.current && draggableContentRef.current.addBlock) {
      draggableContentRef.current.addBlock(block.content, position);
    } else {
      // Fallback to the old method
      let updatedContent;

      if (position === "end") {
        // Add to the end of the lesson plan
        updatedContent = lessonContent + "\n\n" + block.content;
      } else {
        // For now, just add to end. Later we can implement positioning
        updatedContent = lessonContent + "\n\n" + block.content;
      }

      setLessonContent(updatedContent);

      // Brief highlight of the added content
      setHighlightedChanges([{ type: "added", content: block.content }]);
      setTimeout(() => setHighlightedChanges([]), 3000);
    }
  };

  // Filter out resource sections from lesson content for clean lesson plan view
  const filterResourcesFromContent = (content) => {
    if (!content) return content;

    // First, remove horizontal rules (--- or ___) that create lines
    let cleanedContent = content
      .replace(/^---+\s*$/gm, "") // Remove horizontal rules (---)
      .replace(/^___+\s*$/gm, "") // Remove horizontal rules (___)
      .replace(/^\*\*\*+\s*$/gm, ""); // Remove horizontal rules (***)

    // Split content into sections
    const sections = cleanedContent.split(/\n\s*\n/);

    // Filter out sections that contain video/resource content
    const filteredSections = sections.filter((section) => {
      const trimmedSection = section.trim();
      if (!trimmedSection) return false; // Remove empty sections

      const lowerSection = trimmedSection.toLowerCase();

      // Remove sections that contain video-related keywords
      const videoKeywords = [
        "khan academy videos",
        "khan academy",
        "video",
        "watch:",
        "youtube.com",
        "ytimg.com",
        "representing a number as a decimal",
        "decimals: comparing place values",
        "additional explanation and practice",
      ];

      // Check if section contains any video keywords
      const containsVideoContent = videoKeywords.some((keyword) =>
        lowerSection.includes(keyword)
      );

      // Also remove sections that are primarily video titles or descriptions
      const isVideoSection =
        /^\d+\.\s+.*\|\s*(decimals|pre-algebra|khan academy)/i.test(
          trimmedSection
        );

      return !containsVideoContent && !isVideoSection;
    });

    // Join sections and clean up any extra whitespace/line breaks
    return filteredSections
      .join("\n\n")
      .replace(/\n{3,}/g, "\n\n") // Replace 3+ consecutive newlines with just 2
      .trim(); // Remove leading/trailing whitespace
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const lessonTitle = `Unit 5.0 - Day ${unitDay}`;

  // Function to calculate relative time
  const getRelativeTime = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInMinutes = Math.floor((now - past) / (1000 * 60));

    if (diffInMinutes < 1) return "Saved just now";
    if (diffInMinutes === 1) return "Saved a minute ago";
    if (diffInMinutes < 60) return `Saved ${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return "Saved an hour ago";
    if (diffInHours < 24) return `Saved ${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "Saved yesterday";
    if (diffInDays < 7) return `Saved ${diffInDays} days ago`;

    return `Saved ${past.toLocaleDateString()}`;
  };

  // Memoize filtered content to prevent unnecessary re-computation
  const filteredLessonContent = useMemo(() => {
    return filterResourcesFromContent(lessonContent);
  }, [lessonContent]);

  // Custom markdown components for better styling
  const markdownComponents = {
    h1: ({ children }) => (
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 700, mt: 3, mb: 2 }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: 600, mt: 2.5, mb: 1.5 }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: 600, mt: 2, mb: 1 }}
      >
        {children}
      </Typography>
    ),
    p: ({ children, ...props }) => {
      // Check if this content should be highlighted
      const content = children?.toString() || "";
      const cleanContent = content.trim();
      const shouldHighlight = highlightedChanges.some(
        (change) =>
          change.exactMatch === cleanContent ||
          (cleanContent &&
            change.exactMatch &&
            cleanContent.includes(change.exactMatch))
      );

      return (
        <Typography
          variant="body1"
          paragraph
          sx={{
            lineHeight: 1.6,
            mb: 2,
            whiteSpace: "pre-wrap",
            display: "block",
            backgroundColor: shouldHighlight ? "#fff3cd" : "transparent",
            padding: shouldHighlight ? "4px 8px" : "0",
            borderRadius: shouldHighlight ? "4px" : "0",
            transition: "background-color 0.3s ease-out",
            animation: shouldHighlight
              ? "highlightFade 3s ease-out forwards"
              : "none",
            "@keyframes highlightFade": {
              "0%": { backgroundColor: "#fff3cd" },
              "70%": { backgroundColor: "#fff3cd" },
              "100%": { backgroundColor: "transparent" },
            },
          }}
        >
          {children}
        </Typography>
      );
    },
    strong: ({ children }) => (
      <Typography component="span" sx={{ fontWeight: 700, display: "inline" }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 3, mb: 2 }}>
        {children}
      </Box>
    ),
    li: ({ children }) => {
      // Check if this list item should be highlighted
      const content = children?.toString() || "";
      const cleanContent = content.trim();
      const shouldHighlight = highlightedChanges.some(
        (change) =>
          change.exactMatch === cleanContent ||
          (cleanContent &&
            change.exactMatch &&
            cleanContent.includes(change.exactMatch))
      );

      return (
        <Typography
          component="li"
          variant="body1"
          sx={{
            mb: 0.5,
            backgroundColor: shouldHighlight ? "#fff3cd" : "transparent",
            padding: shouldHighlight ? "2px 4px" : "0",
            borderRadius: shouldHighlight ? "4px" : "0",
            transition: "background-color 0.3s ease-out",
            animation: shouldHighlight
              ? "highlightFade 3s ease-out forwards"
              : "none",
            "@keyframes highlightFade": {
              "0%": { backgroundColor: "#fff3cd" },
              "70%": { backgroundColor: "#fff3cd" },
              "100%": { backgroundColor: "transparent" },
            },
          }}
        >
          {children}
        </Typography>
      );
    },
    br: () => <br />,
    // Handle line breaks within paragraphs
    text: ({ children }) => children,
    // Handle YouTube video links - redirect to Resources tab instead of embedding
    a: ({ href, children }) => {
      // Check if this is a YouTube link
      if (href && href.includes("youtube.com/watch")) {
        return (
          <Box
            sx={{
              my: 2,
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              backgroundColor: theme.palette.action.hover,
              cursor: "pointer",
            }}
            onClick={() => setActiveTab("resources")}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.primary.main }}
            >
              📺 {children}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click to view video resources →
            </Typography>
          </Box>
        );
      }
      // Regular link
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: theme.palette.primary.main }}
        >
          {children}
        </a>
      );
    },
    // Handle YouTube thumbnail images that link to videos
    img: ({ src, alt }) => {
      // Check if this is a YouTube thumbnail
      if (src && src.includes("ytimg.com")) {
        return (
          <Box
            sx={{
              display: "inline-block",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 1,
              maxWidth: "200px",
              cursor: "pointer",
            }}
          >
            <img
              src={src}
              alt={alt}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
          </Box>
        );
      }
      // Regular image
      return (
        <img src={src} alt={alt} style={{ maxWidth: "100%", height: "auto" }} />
      );
    },
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Helmet>
        <title>{`Maestro - ${lessonTitle}`}</title>
        <meta name="description" content={`Lesson plan for ${lessonTitle}`} />
      </Helmet>

      <MaestroNavbar />

      {/* Action Bar */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: { xs: 1.5, sm: 2 },
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent={{ xs: "flex-start", sm: "space-between" }}
          sx={{ maxWidth: "100%", overflow: "hidden" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{ color: "text.primary", flexShrink: 0 }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: { xs: "nowrap", sm: "normal" },
              }}
            >
              {lessonTitle}
            </Typography>
            {loadedFromSaved && savedLessonTimestamp && (
              <Tooltip title="This is your saved, customized version">
                <Chip
                  label={getRelativeTime(savedLessonTimestamp)}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {hasUnsavedChanges && (
              <Tooltip title="Auto-saving changes...">
                <Chip
                  label="Unsaved Changes"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Box>

          {!loading && !error && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "center", sm: "center" }}
              sx={{
                width: { xs: "100%", sm: "auto" },
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {loadedFromSaved && (
                <Tooltip title="Load the original lesson plan from Maestro">
                  <Button
                    variant="text"
                    startIcon={<RefreshIcon />}
                    onClick={handleLoadFresh}
                    size="small"
                    sx={{
                      color: "text.secondary",
                      width: { xs: "auto", sm: "auto" },
                      flexShrink: 0,
                    }}
                  >
                    Start Over
                  </Button>
                </Tooltip>
              )}
              {!isMobile && !isLargeScreen && (
                <Button
                  variant="outlined"
                  startIcon={<ChatIcon />}
                  onClick={() => setChatOpen(!chatOpen)}
                  size="small"
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  Ask AI
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                size="small"
                sx={{
                  width: { sm: "auto" },
                  alignSelf: { xs: "center", sm: "auto" },
                  flexShrink: 0,
                }}
              >
                Print Lesson
              </Button>
              {saveStatus && (
                <Typography
                  variant="body2"
                  sx={{
                    color: saveStatus.includes("Error")
                      ? "error.main"
                      : "success.main",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    textAlign: { xs: "center", sm: "right" },
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {saveStatus}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Main Content Area - Flexbox Layout */}
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          height: "calc(100vh - 140px)", // Account for navbar and action bar
          overflow: "hidden",
        }}
      >
        {/* Lesson Content - Scrollable Left Side */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            pt: 0, // Remove top padding to eliminate whitespace above tabs
            px: { xs: 2, sm: 3 }, // Keep horizontal padding
            pb: { xs: 2, sm: 3 }, // Keep bottom padding
            boxSizing: "border-box",
            // Only show chat on desktop screens wider than 1200px
            display: { xs: "block", md: !isMobile ? "block" : "block" },
          }}
        >
          <Box
            ref={printRef}
            sx={{
              maxWidth: { xs: "100%", md: "800px" },
              margin: "0 auto",
              "@media print": {
                maxWidth: "none",
                margin: 0,
                padding: "0.5in",
              },
            }}
          >
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <CircularProgress size={40} />
                  <Typography variant="body1" color="text.secondary">
                    Generating differentiated lesson plan for Day {unitDay}...
                  </Typography>
                </Stack>
              </Box>
            )}

            {error && (
              <Card
                sx={{
                  p: 3,
                  bgcolor: "error.light",
                  color: "error.contrastText",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Error Loading Lesson Plan
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  onClick={fetchLessonPlan}
                  sx={{
                    bgcolor: "error.dark",
                    "&:hover": { bgcolor: "error.main" },
                  }}
                >
                  Try Again
                </Button>
              </Card>
            )}

            {!loading && !error && lessonContent && (
              <Box sx={{ width: "100%" }}>
                {/* Tabs */}
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    mb: 2,
                    zIndex: 10,
                    "& .MuiTab-root": {
                      fontWeight: 600,
                    },
                  }}
                >
                  <Tab label="Lesson Plan" value="lesson-plan" disableRipple />
                  <Tab label="Resources" value="resources" disableRipple />
                </Tabs>

                {/* Lesson Plan Tab */}
                <Box role="tabpanel" hidden={activeTab !== "lesson-plan"}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      borderRadius: "20px",
                      boxShadow: theme.shadows.cardLg
                        ? theme.shadows.cardLg(
                            theme.palette,
                            theme.palette.mode
                          )
                        : theme.shadows[8],
                      backgroundColor: theme.palette.background.paper,
                      border: "none",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: theme.shadows.cardLg
                          ? theme.shadows.cardLg(
                              theme.palette,
                              theme.palette.mode
                            )
                          : theme.shadows[12],
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <DraggableLessonContent
                        ref={draggableContentRef}
                        lessonContent={filteredLessonContent}
                        setLessonContent={setLessonContent}
                        markdownComponents={markdownComponents}
                        highlightedChanges={highlightedChanges}
                        setHighlightedChanges={setHighlightedChanges}
                      />
                    </CardContent>
                  </Card>
                </Box>

                {/* Resources Tab */}
                <Box role="tabpanel" hidden={activeTab !== "resources"}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: "20px",
                      boxShadow: theme.shadows.cardLg
                        ? theme.shadows.cardLg(
                            theme.palette,
                            theme.palette.mode
                          )
                        : theme.shadows[8],
                      backgroundColor: theme.palette.background.paper,
                      border: "none",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <LessonResources
                        unitDay={unitDay}
                        lessonContent={lessonContent}
                      />
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Chat Sidebar on Desktop - Right Side Flexbox */}
        {isLargeScreen && (
          <Box
            sx={{
              width: "420px",
              flexShrink: 0,
              borderLeft: `2px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              boxShadow: `-4px 0 12px rgba(0,0,0,0.1)`,
              display: "flex",
              flexDirection: "column",
              // Hide on smaller desktop screens
              display: { xs: "none", lg: "flex" },
            }}
          >
            <LessonChat
              lessonContent={lessonContent}
              setLessonContent={setLessonContent}
              unitDay={unitDay}
              setHighlightedChanges={setHighlightedChanges}
              addBlockToLessonPlan={addBlockToLessonPlan}
              isOpen={true}
              onToggle={() => setChatOpen(!chatOpen)}
            />
          </Box>
        )}
      </Box>

      {/* Mobile & Medium Screen Chat - render as popup */}
      {(isMobile || (!isLargeScreen && !isMobile)) && (
        <LessonChat
          lessonContent={lessonContent}
          setLessonContent={setLessonContent}
          unitDay={unitDay}
          setHighlightedChanges={setHighlightedChanges}
          addBlockToLessonPlan={addBlockToLessonPlan}
          isOpen={chatOpen}
          onToggle={() => setChatOpen(!chatOpen)}
        />
      )}
    </div>
  );
};

export default LessonPlan;
